import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Table, Caption, Thead, Tr, Th, Tbody, Td } from '@patternfly/react-table';
import { Button, Alert, ValidatedOptions } from '@patternfly/react-core';
import { BulkCheckItem, BulkCheckResult, apiBulkCheck, apiFetchBusinessData } from '../helpers/apiHelpers';
import { useStableAccessToken } from '../hooks/useStableAccessToken';
import ReviewRow from './ReviewRow';
// import { gatherAllCheckedAndRequired, gatherAllForNonGranular } from '../helpers/depsHelper';
import { computeRequiredMap, hasAllRequired, getStoredLabelsInfo, buildLabelsIndex } from '../helpers/labels';
import { useFinalDeps } from '../hooks/useFinalDeps';
import { useBulkCheck } from '../hooks/useBulkCheck';
import { useReviewPersistence } from '../hooks/useReviewPersistence';
import { useBranchFallbackLabels } from '../hooks/useBranchFallbackLabels';
import { ServicesTable } from './ServicesTable';
import { LabelsValidator } from './LabelsValidator';
import { InfraTable } from './InfraTable';


import { ReviewStepProps } from './types';
import { useDeployRepoParams } from '../hooks/useDeployRepoParams';
import { Link } from 'react-router-dom';

const ReviewStep: React.FC<ReviewStepProps> = ({
  selectedBusinessUnits,
  checkboxStates,
  businessData,
  deploymentBranch,
  deployRepoBranch,
  applicationBranch,
  team,
  namespace,
  location,
  buildRailsEnv,
  standalone,
  envClass,
  setDisableDeploy,
  lastBuiltImage,
  granularDeployment,
  extraEnvVars,
  enableNewRelic,
  downmergeMaster,
  enableRegressionProfile,
  authError,
  initialBulkCheckItems,
  skipQuota,
  labelsByApp,
  deployedServices,
  isRefresh,
}) => {
  const tokenGetter = useStableAccessToken();
  const [branchMapping, setBranchMapping] = useState<Record<string, string>>({});
  const [deployTagMapping, setDeployTagMapping] = useState<Record<string, string>>({});
  const [localLabelsByApp, setLocalLabelsByApp] = useState(labelsByApp);
  
  // if we got a list straight from Manage-env → use it
  const manualDeps = useRef(
    Array.isArray(initialBulkCheckItems) && initialBulkCheckItems.length > 0
      ? initialBulkCheckItems
      : undefined
  );
  const { finalServiceDeps, finalInfraDeps } = useFinalDeps({
    businessData,
    checkboxStates,
    granularDeployment,
    skipQuota,
    selectedBusinessUnits,
    applicationBranch,
    deploymentBranch,
    deployedServices,
    manualDeps: manualDeps.current
  });
  const { bulkResults, loading, error, setBulkResults } = useBulkCheck({
    finalServiceDeps,
    applicationBranch,
    initialBulkCheckItems
  });
  const hasCalledRef = useRef(false);
  const [refreshingApp, setRefreshingApp] = useState<string|null>(null);
  const [imageValidationMap, setImageValidationMap] = useState<Record<string,'found'|'not found'>>({});
  const [hasMissingLabels, setHasMissingLabels] = useState(false);
  
  const deployRepoParams = useDeployRepoParams(
    isRefresh ? undefined : deployRepoBranch, // On refresh, let api response decide branch
    isRefresh ? namespace : undefined, // On refresh, provide a namespace to analyze
    isRefresh ? initialBulkCheckItems : finalServiceDeps // On refresh, take the apps from the storage list
  );

  // final lists for services and infra are provided by useFinalDeps
  const [showInfra, setShowInfra] = useState(false);

  const prevBulkRef = useRef<BulkCheckResult[]>([]);

  const norm = (s: string) => s ? s.replace(/\s*\(.+?\)\s*$/, '') : s;

  const effectiveStandalone = skipQuota;

  // 1) manualDeps is handled in useFinalDeps

  // 2) Reset hasCalledRef
  useEffect(() => {
    hasCalledRef.current = false;
  }, [finalServiceDeps, finalInfraDeps]);

  // 2.1) Update local labels when prop changes
  useEffect(() => {
    setLocalLabelsByApp(labelsByApp);
  }, [labelsByApp]);

  // 3) Update branchMapping
  useEffect(() => {
    setBranchMapping((prev) => {
      const updated = { ...prev };
      [...finalServiceDeps, ...finalInfraDeps].forEach((item) => {
        if (!updated[item.name]) {
          updated[item.name] = item.branch;
        }
      });
      return updated;
    });
  }, [finalServiceDeps, finalInfraDeps]);

  // manual refresh list handled inside useBulkCheck

  // 4) Map bulk results repoName->app name
  useEffect(() => {
    if (!finalServiceDeps.length || !bulkResults.length) return;
    const repoToAppMap = new Map(finalServiceDeps.map(dep => [dep.repoName, dep.name]));
    const results = bulkResults.map(result => ({
      ...result,
      application: repoToAppMap.get(result.application) || result.application
    }));
    setBulkResults(results);
  }, [finalServiceDeps]);

  // 5) If backend changed branch, update mapping
  useEffect(() => {
    if (!bulkResults.length) return;
    setBranchMapping((prev) => {
      const updated = { ...prev };
      for (const res of bulkResults) {
        if (res.branch && res.branch !== updated[res.application]) {
          updated[res.application] = res.branch;
        }
      }
      return updated;
    });
  }, [bulkResults]);

  // 5.1) Fetch labels for apps that fell back to a different branch
  useBranchFallbackLabels({
    bulkResults,
    applicationBranch,
    deploymentBranch,
    onLabelsUpdate: (updates) => {
      setLocalLabelsByApp(prev => ({
        ...prev,
        ...updates
      }));
    }
  });

  // single refresh
  const refreshBulkResultForApp = async (app: string, newBranch: string) => {
    setRefreshingApp(app);
    try {
      const repoNameMap = new Map(finalServiceDeps.map(dep => [dep.name, dep.repoName]));
      const appNameMap = new Map(finalServiceDeps.map(dep => [dep.repoName, dep.name]));
      const repoName = repoNameMap.get(app) || app;
  
      // Fetch bulk check results
      const { results: updatedResults } = await apiBulkCheck([{ appName: app, repoName, branch: newBranch }], tokenGetter);

      const remapped = updatedResults.map(res => ({
        ...res,
        application: appNameMap.get(res.application) || res.application,
      }));
  
      setBulkResults((prev) => {
        const updated = prev.map((item) =>
          item.application === app ? remapped[0] : item
        );
        const exists = updated.some(item => item.application === app);
        return exists ? updated : [...updated, remapped[0]];
      });
      
      // auto-set deployTagMapping for refreshed app
      if (remapped[0]?.images?.deploy_tags?.[0]) {
        const latestTag = remapped[0].images.deploy_tags[0].replace(/^commit-/, '');
        setDeployTagMapping(prev => ({ ...prev, [app]: latestTag }));
      }

      // Fetch labels for the new branch
      try {
        const branchData = await apiFetchBusinessData(newBranch, tokenGetter, deploymentBranch);
        const newLabels = buildLabelsIndex(branchData);
        
        // Update only the labels for this specific app
        setLocalLabelsByApp(prev => ({
          ...prev,
          [app]: newLabels[app] || { found: [], domainValue: null }
        }));
      } catch (labelError) {
        console.warn('Error fetching labels for', app, 'on branch', newBranch, labelError);
        // If labels fetch fails, clear labels for this app
        setLocalLabelsByApp(prev => ({
          ...prev,
          [app]: { found: [], domainValue: null }
        }));
      }
    } catch (error) {
      console.error('Error refreshing bulk result for', app, error);
    } finally {
    setRefreshingApp(null);
   }
  };

  // compute labels info early for both persistence and validation - memoize to update when deps change
  const storedLabelsInfo = useMemo(() => 
    getStoredLabelsInfo(finalServiceDeps, finalInfraDeps, localLabelsByApp),
    [finalServiceDeps, finalInfraDeps, localLabelsByApp]
  );

  // 6) Save final data to localStorage (extracted)
  useReviewPersistence({
    businessData,
    finalServiceDeps,
    finalInfraDeps,
    bulkResults,
    branchMapping,
    deployTagMapping,
    selectedBusinessUnits,
    deploymentBranch,
    deployRepoBranch: deployRepoParams.selected,
    applicationBranch,
    team,
    namespace,
    location,
    buildRailsEnv,
    standalone,
    envClass,
    lastBuiltImage,
    downmergeMaster,
    extraEnvVars,
    enableNewRelic,
    enableRegressionProfile,
    labelsByApp: localLabelsByApp,
    storedLabelsInfo,
  });

  // 7) Disable deploy if any service is missing an image
  useEffect(() => {
    const next = { ...deployTagMapping };
    let changed = false;

    bulkResults.forEach(r => {
      const prev = prevBulkRef.current.find(p => p.application === r.application);
      if (!prev || prev.branch !== r.branch) {
        next[r.application] = ''; // reset on branch change
        changed = true;
      }
    });

    prevBulkRef.current = bulkResults;
    if (changed) setDeployTagMapping(next);
  }, [bulkResults]);

  // 2) recompute found/not-found whenever results OR selection changes
  useEffect(() => {
    const nextValidation: Record<string, 'found' | 'not found'> = {};

    bulkResults.forEach(r => {
      const dt = r.images?.deploy_tags ?? [];   // e.g. ['commit-abc', ...]
      const ft = r.images?.found_tags ?? [];    // e.g. ['commit-abc']
      const picked = deployTagMapping[r.application]; // e.g. 'abc'

      const sel = picked ? `commit-${picked}` : dt[0];

      // treat "no found_tags but user picked one" as valid (old behavior)
      const valid =
        dt.length > 0 &&
        (ft.length === 0 ? !!picked : ft.includes(norm(sel)));

      nextValidation[r.application] = valid ? 'found' : 'not found';
    });

    setImageValidationMap(nextValidation);
  }, [bulkResults, deployTagMapping]);

  // 3) disable deploy based on current validation
  useEffect(() => {
    // Determine whether any required services are missing images
    const serviceNames = finalServiceDeps.map(d => d.name);
    const hasMissingServiceImages = serviceNames.some(name => imageValidationMap[name] === 'not found');

    // Disable deploy if loading, any service is missing images, missing labels, or deploy repo is invalid
    setDisableDeploy(loading || hasMissingServiceImages || hasMissingLabels || deployRepoParams.validating || deployRepoParams.valid === 'error');
  }, [loading, imageValidationMap, finalServiceDeps, hasMissingLabels, setDisableDeploy, deployRepoParams.validating, deployRepoParams.valid]);

  if (loading || deployRepoParams.validating) {
    return <div className="dx-flex dx-flex--column dx-flex--align-start dx-flex--justify-start" style={{ height: '200px' }}>
              <div className="dx-spinner" aria-label="validation checks" />
              <h3 className="dx-title" style={{ marginTop: '1rem' }}>
              Running validation checks…
              </h3>
            </div>
  }
  if (error) {
    return (
      <Alert variant="danger" title="Validation failed" isInline>
        {error.includes('rate limit')
          ? 'GitHub API rate limit exceeded. Please try again in a few minutes.'
          : error}
      </Alert>
    );
  }
  if (bulkResults.length === 0 && finalServiceDeps.length === 0 && finalInfraDeps.length === 0) {
    return <p>No data loaded (or no dependencies selected).</p>;
  }

  // Build a lookup of “owner/repo” from your metadata
  const repoFullMap = Object.fromEntries(
    businessData.releases.map((r: any) => [r.name, r.repository])
  );
  // Sort service results for display
  const sortedServiceResults = bulkResults
    .filter(res => {
      const rel = businessData.releases.find((r: any) => r.name === res.application);
      return !rel || rel.type !== 'infrastructure';
    })
    .sort((a, b) => {
      if (a.exists === b.exists) return 0;
      return a.exists ? -1 : 1;
    });

  const showLatestImages = sortedServiceResults.some(
    res => (res.images?.found_tags || []).length === 0
  );
  const repoNameMap = Object.fromEntries(finalServiceDeps.map(dep => [dep.name, dep.repoName]));

  const isRefreshFlow = Boolean(initialBulkCheckItems && initialBulkCheckItems.length);
  const anyBehind = bulkResults.some(r => r.behindInfo?.behind_by! > 0);

  // Labels validation UI handled by LabelsValidator

  return (
    <div className="dx-flex dx-flex--column dx-flex--gap-md">
      <div>
        <h2 className="dx-title">
          Review applications to deploy
        </h2>
        <div className="review-meta-grid">
          <span className="review-meta-label">Medulla branch</span>
          <span className="review-meta-sep">:</span>
          <span className="review-meta-value">{deploymentBranch || <i>No medulla branch selected</i>}</span>

          <span className="review-meta-label">Feature branch</span>
          <span className="review-meta-sep">:</span>
          <span className="review-meta-value">{applicationBranch || <i>No feature branch specified</i>}</span>

          <span className="review-meta-label">Deploy repo branch</span>
          <span className="review-meta-sep">:</span>
          <span className="review-meta-value">
            {deployRepoParams.selected}
            {deployRepoParams.valid === 'error' &&
              <><br/><span style={{ color: 'var(--accent-red, red)' }}>{deployRepoParams.message}. Environment refresh is disabled. Use <Link to='/granular-deploy'>Manage Environment</Link> to choose a specific deploy repo branch.</span></>
            }
          </span>

          <span className="review-meta-label">Namespace</span>
          <span className="review-meta-sep">:</span>
          <span className="review-meta-value">{namespace || <i>No namespace specified</i>}</span>
        </div>
      </div>
        {isRefreshFlow && anyBehind && (
            <div>
              <Alert
                variant="warning"
                isInline
                title="Please bear in mind your feature branch is not up to date with master; please rebase your branch to pull the latest changes by clicking the Update button."
                style={{ marginBottom: '1rem' }}
              />
            </div>
          )}
      <LabelsValidator
        finalServiceDeps={finalServiceDeps}
        storedLabelsInfo={storedLabelsInfo as any}
        labelsByApp={localLabelsByApp as any}
        onMissingLabelsChange={setHasMissingLabels}
      />
      {/* SERVICES */}
      {sortedServiceResults.length > 0 && (
        <div>
          <ServicesTable
            sortedServiceResults={sortedServiceResults}
            businessData={businessData}
            repoNameMap={repoNameMap}
            repoFullMap={repoFullMap}
            branchMapping={branchMapping}
            applicationBranch={applicationBranch}
            deployTagMapping={deployTagMapping}
            setBranchMapping={setBranchMapping}
            setDeployTagMapping={setDeployTagMapping}
            imageValidationMap={imageValidationMap}
            setImageValidationMap={setImageValidationMap}
            refreshingApp={refreshingApp}
            refreshBulkResultForApp={refreshBulkResultForApp}
            checkboxStates={checkboxStates}
            showLatestImages={showLatestImages}
            versionsByApp={Object.fromEntries(sortedServiceResults.map(r => [r.application, r.images?.versions || {}])) as any}
            labelsByApp={localLabelsByApp}
            storedLabelsInfo={storedLabelsInfo as any}
          />
        </div>
      )}

      {/* INFRA */}
       {finalInfraDeps.length > 0 && (
      <div>
        <Button variant="link" onClick={() => setShowInfra((prev) => !prev)}>
          {showInfra ? 'Hide' : 'Show'} Infrastructure Dependencies
        </Button>
        {showInfra && (
          <InfraTable
            finalInfraDeps={finalInfraDeps}
            branchMapping={branchMapping}
            deploymentBranch={deploymentBranch}
          />
        )}
      </div>
    )}
    {finalServiceDeps.some(d => imageValidationMap[d.name] === 'not found') && (
      <p style={{ color: 'red', marginTop: '10px' }}>
        Please make sure all images above are valid and all the apps have relevant labels before proceeding with deploy
      </p>
    )}
    {authError && (
        <div>
          <Alert variant="danger" title={authError} />
        </div>
      )}
    </div>
  );
};

export default ReviewStep;
